import React, { FC, memo, useMemo, useState, useCallback } from 'react';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getSelectedSizeCodeId, getWarehouseInfo } from 'pages/BookingPage';
import { SizeCodeForBooking, useGetSizeCodesForBookingQuery } from 'entities/SizeCode';
import { useGetRentOptionsQuery } from 'entities/RentOption';
import { ModernBoxSizeFilterMobile } from 'features/ModernBoxSizeFilter';
import { SizeCodeCardMobile } from 'features/ModernSizeCodeSelect/ui/SizeCodeCardMobile';
import { BoxSelectionModalMobile } from 'features/ModernSizeCodeSelect/ui/BoxSelectionModalMobile';
import { Box } from 'entities/Box';
import { getSelectedFilter } from 'features/BoxSizeFilter';

interface ModernSelectBoxSizeContentMobileProps {
  selectedBoxId: string | undefined;
  onSelectBox: (box: Box, sizeCodeId: string) => void;
  onCompleteStep: (box: Box, sizeCodeId: string) => void;
}

export const ModernSelectBoxSizeContentMobile: FC<ModernSelectBoxSizeContentMobileProps> = memo((props) => {
  const { selectedBoxId, onSelectBox, onCompleteStep } = props;

  const warehouseInfo = useAppSelector(getWarehouseInfo);
  const selectedWarehouseId = warehouseInfo?.warehouseId;
  const selectedSizeCodeId = useAppSelector(getSelectedSizeCodeId);
  const boxSizeFilter = useAppSelector(getSelectedFilter);

  const [selectedSizeCodeForModal, setSelectedSizeCodeForModal] = useState<SizeCodeForBooking | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: sizeCodes } = useGetSizeCodesForBookingQuery({ warehouseId: selectedWarehouseId }, { skip: !selectedWarehouseId });
  const { data: rentOptions } = useGetRentOptionsQuery({ warehouseId: selectedWarehouseId }, { skip: !selectedWarehouseId });

  const sortedSizeCodes = useMemo(() => {
    if (!sizeCodes) return undefined;
    return [...sizeCodes].sort((a, b) => a.square - b.square);
  }, [sizeCodes]);

  const filterBySizeCode = useMemo<SizeCodeForBooking[]>(() => {
    if (!sortedSizeCodes) return [];

    return sortedSizeCodes?.filter((sizeCode) => (boxSizeFilter === 'All' ? true : boxSizeFilter === sizeCode.sizeGroup));
  }, [boxSizeFilter, sortedSizeCodes]);

  const availableSizeCodes = useMemo(() => {
    if (!filterBySizeCode) return [];
    return filterBySizeCode.filter((sizeCode) => sizeCode.availableBoxesCount > 0);
  }, [filterBySizeCode]);

  const handleSizeCodeClick = useCallback((sizeCode: SizeCodeForBooking) => {
    setSelectedSizeCodeForModal(sizeCode);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedSizeCodeForModal(null);
  }, []);

  const handleSelectBox = useCallback(
    (box: Box) => {
      if (selectedSizeCodeForModal) {
        onSelectBox(box, selectedSizeCodeForModal.sizeCodeId);
      }
    },
    [selectedSizeCodeForModal, onSelectBox],
  );

  const handleBookBox = useCallback(
    (box: Box) => {
      if (selectedSizeCodeForModal) {
        onCompleteStep(box, selectedSizeCodeForModal.sizeCodeId);
        handleCloseModal();
      }
    },
    [selectedSizeCodeForModal, onCompleteStep, handleCloseModal],
  );

  return (
    <>
      <div className="space-y-6">
        <div className="w-full overflow-hidden">
          <ModernBoxSizeFilterMobile sizeCodes={availableSizeCodes} />
        </div>

        <div className="space-y-4">
          {availableSizeCodes.map((sizeCode) => (
            <SizeCodeCardMobile
              key={sizeCode.sizeCodeId}
              sizeCode={sizeCode}
              isSelected={selectedSizeCodeId === sizeCode.sizeCodeId}
              onClick={() => {
                handleSizeCodeClick(sizeCode);
              }}
            />
          ))}
        </div>
      </div>

      {selectedSizeCodeForModal && (
        <BoxSelectionModalMobile
          isOpen={isModalOpen}
          sizeCode={selectedSizeCodeForModal}
          rentOptions={rentOptions || []}
          selectedBoxId={selectedBoxId}
          onClose={handleCloseModal}
          onSelectBox={handleSelectBox}
          onBookBox={handleBookBox}
        />
      )}
    </>
  );
});
